/**
 * @description Token 监控插件
 * @description 监听页面可见性、焦点变化、路由切换等事件，主动检测和刷新 token
 * @reason 1小时过期的token需要更主动的检测机制，避免用户长时间不操作导致token过期
 */

const TOKEN_MONITOR_CLEANUP_KEY = '__TENDA_TOKEN_MONITOR_CLEANUP__'

export default defineNuxtPlugin((nuxtApp) => {
  // 只在客户端执行
  if (!import.meta.client) return

  // HMR 或应用重复挂载前，先释放上一次注册的监听器和定时器。
  window[TOKEN_MONITOR_CLEANUP_KEY]?.()

  const userStore = useUserStore()
  userStore.restoreImsTokenFromCookies()
  let visibilityCheckTimer = null
  let initialCheckTimer = null
  let lastCheckTime = 0
  const CHECK_INTERVAL = 5 * 60 * 1000 // 5分钟检查一次（即使页面可见）

  /**
   * @description 检查并刷新 token（如果需要）
   * @param {string} reason - 触发原因，用于日志
   */
  const checkAndRefreshToken = async (reason) => {
    userStore.restoreImsTokenFromCookies()

    // 如果用户未登录，不需要检查
    if (!userStore.isLoggedIn || !userStore.refreshToken) {
      return
    }

    // 防止频繁检查（至少间隔30秒）
    const now = Date.now()
    if (now - lastCheckTime < 30 * 1000) {
      return
    }
    lastCheckTime = now

    try {
      // 检查 refresh_token 是否过期
      if (userStore.isRefreshTokenExpired) {
        console.warn(`[Token Monitor] ${reason}: refresh_token 已过期，退出登录`)
        // 统一处理认证失效跳转（内部包含清理登录态逻辑）
        const { handleUnauthorized } = useAuthLinks()
        await handleUnauthorized({ forceRedirect: true, redirectToLogin: true })
        return
      }

      // 检查 access_token 是否进入 IMS 约定的到期前 60 秒刷新窗口
      if (userStore.shouldRefreshAccessToken) {
        console.warn(`[Token Monitor] ${reason}: access_token 已过期或即将过期，开始刷新`)
        try {
          await userStore.refreshAccessToken()
          console.warn(`[Token Monitor] ${reason}: token 刷新成功`)
        } catch (error) {
          console.error(`[Token Monitor] ${reason}: token 刷新失败:`, error)
          const { handleUnauthorized } = useAuthLinks()
          await handleUnauthorized({ forceRedirect: true, redirectToLogin: true })
        }
      }
    } catch (error) {
      console.error(`[Token Monitor] ${reason}: 检查 token 时发生错误:`, error)
    }
  }

  /**
   * @description 处理页面可见性变化
   */
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      // 页面变为可见时，检查 token
      checkAndRefreshToken('页面可见性变化')
    }
  }

  /**
   * @description 处理窗口焦点变化
   */
  const handleFocus = () => {
    // 窗口获得焦点时，检查 token
    checkAndRefreshToken('窗口获得焦点')
  }

  /**
   * @description 启动定期检查定时器
   */
  const startPeriodicCheck = () => {
    // 清除旧的定时器
    if (visibilityCheckTimer) {
      clearInterval(visibilityCheckTimer)
    }

    // 设置定期检查（每5分钟检查一次，即使页面一直可见）
    visibilityCheckTimer = setInterval(() => {
      if (document.visibilityState === 'visible') {
        checkAndRefreshToken('定期检查')
      }
    }, CHECK_INTERVAL)
  }

  /**
   * @description 停止定期检查定时器
   */
  const stopPeriodicCheck = () => {
    if (visibilityCheckTimer) {
      clearInterval(visibilityCheckTimer)
      visibilityCheckTimer = null
    }
  }

  // 监听页面可见性变化
  document.addEventListener('visibilitychange', handleVisibilityChange)

  // 监听窗口焦点变化
  window.addEventListener('focus', handleFocus)

  // 启动定期检查
  startPeriodicCheck()

  // 页面卸载时清理
  const cleanup = () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('focus', handleFocus)
    window.removeEventListener('beforeunload', cleanup)
    stopPeriodicCheck()
    if (initialCheckTimer) {
      window.clearTimeout(initialCheckTimer)
      initialCheckTimer = null
    }
    if (window[TOKEN_MONITOR_CLEANUP_KEY] === cleanup) {
      delete window[TOKEN_MONITOR_CLEANUP_KEY]
    }
  }

  // 监听页面卸载
  window[TOKEN_MONITOR_CLEANUP_KEY] = cleanup
  window.addEventListener('beforeunload', cleanup)
  nuxtApp.hook('app:beforeUnmount', cleanup)

  // 初始检查（页面加载完成后）
  if (userStore.isLoggedIn && userStore.refreshToken) {
    // 延迟一下，确保 store 已完全初始化
    initialCheckTimer = window.setTimeout(() => {
      initialCheckTimer = null
      checkAndRefreshToken('页面初始化')
    }, 1000)
  }
})
